import { COMMUNICATION_STATUS_BUCKETS } from "@/type/api";

/** The badge that stands for a communication's status: the word, the icon and
 * the Bootstrap colour it is drawn in. */
export interface CommunicationStatusBadge {
	label: string;
	icon: string; // bootstrap-icons class
	variant: string; // bootstrap text-bg-* suffix
}

/**
 * One badge per finished status. The statuses themselves are not stated here:
 * the key set is the closed bucket, so a status the vocabulary adds to that
 * bucket is badged by adding its badge row, and the badge and the filter cannot
 * disagree about which statuses are finished.
 */
const FINISHED_STATUS_BADGES: Record<string, CommunicationStatusBadge> = {
	closed: { label: "Closed", variant: "secondary", icon: "bi-check2-circle" },
	resolved: { label: "Resolved", variant: "success", icon: "bi-check-circle" },
	"possible-issue": {
		label: "Possible Issue",
		variant: "warning",
		icon: "bi-exclamation-triangle",
	},
	"possible-resolved": {
		label: "Possibly Resolved",
		variant: "info",
		icon: "bi-question-circle",
	},
	invalid: { label: "Invalid", variant: "danger", icon: "bi-x-circle" },
};

/** The badge for a status, or null for a status that needs none. Open statuses
 * return null on purpose: the queue stays unbadged so only finished work is
 * called out. A status the vocabulary does not know returns null too, so a
 * status the API grows later never renders a broken badge. */
export function communicationStatusBadge(
	status: string,
): CommunicationStatusBadge | null {
	const finished: readonly string[] = COMMUNICATION_STATUS_BUCKETS.closed;
	if (!finished.includes(status)) {
		return null;
	}
	return FINISHED_STATUS_BADGES[status] ?? null;
}
